/**
 * V2 of the AddEditConfigurationComponent
 * This component is controlled by the EnableAiTrustLayerByoLlmConfigurationsV2 feature flag.
 * Modify this file to implement the new UI design.
 */
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    useNavigateWithParams,
    useShowDialog,
} from '@experiences/util';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApAlertBar,
    ApButton,
    ApLink,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Controller,
    useForm,
    useWatch,
} from 'react-hook-form';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { routePaths } from '../../../common/constants/routePaths';
import { useDocumentationLinks } from '../../../common/hooks/useDocumentationLink';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import type {
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    ByoProductLlmConfiguration,
} from '../../../services/llm-gateway/LlmGateway.types';
import {
    ByoFormErrorTypes,
    ByoLlmConfigurationType,
    LlmConnectorType,
    ModelsConfigurationOption,
} from '../../../services/llm-gateway/LlmGateway.types';
import {
    checkIsFormComplete,
    getConnectorTemplateFromTags,
    getFeatureDescriptionTranslationKey,
    translationKeyExists,
} from '../../../services/llm-gateway/LlmGateway.utils';
import {
    addByoProductLlmConfiguration,
    updateByoProductLlmConfiguration,
    validateByoProductLlmConfiguration,
} from '../../../services/llm-gateway/LlmGatewayService';
import { accountGlobalId } from '../../../store/selectors';
import UiForm from '../../common/UiForm';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../../organizationsettings/AdminBreadCrumbs';
// Reuse validation functions from the original component
import {
    detectIncompleteLlmConfigurationErrors,
    processValidationResults,
} from './AddEditConfigurationComponent';
import type { AddEditConfigurationFormData } from './AddEditConfigurationViewModel';
import { useAddEditConfigurationViewModelV2 } from './AddEditConfigurationViewModelV2';
import { ConnectedModelsIssuesDialogBody } from './subcomponents/ConnectedModelsIssuesDialogBody';
// V2 subcomponents
import { AddEditConfigurationHeaderSection } from './subcomponentsV2/AddEditConfigurationHeaderSection';
import { AllModelsConfigurationSection } from './subcomponentsV2/AllModelsConfigurationSection';
import { AnyModelConfigurationSection } from './subcomponentsV2/AnyModelConfigurationSection';
import { ConnectedModelsSuccessDialogBody } from './subcomponentsV2/ConnectedModelsSuccessDialogBody';
import { FolderDropdown } from './subcomponentsV2/FolderDropdown';

export const detectModelOverlapErrors = (
    featureConfig: ByoOperationGroupConfiguration,
    llmConfigs: Array<Partial<ByoLlmConfiguration>>,
    originalLlmConfigs: ByoLlmConfiguration[],
    inHouseModelNames: string[],
    customerModelNames: string[]): [string, string] | null => {
    // No overlap checks needed for AnyModel configuration
    if (featureConfig.modelsConfigurationOption === ModelsConfigurationOption.AnyModel) {
        return null;
    }

    for (let i = 0; i < llmConfigs.length; i++) {
        const modelName = llmConfigs[i].desiredModel;
        const initialModelName = originalLlmConfigs.at(i)?.desiredModel;
        const isCustomAlias = llmConfigs[i].isCustomAlias;
        const initialIsCustomAlias = !inHouseModelNames.includes(originalLlmConfigs.at(i)?.desiredModel ?? '');

        // No model name to check
        if (!modelName) {
            continue;
        }

        // Consider initial value correct
        if (modelName === initialModelName && isCustomAlias === initialIsCustomAlias) {
            continue;
        }

        // If it is not a custom alias, the config is a like-for-like replacement
        if (!isCustomAlias) {
            continue;
        }

        if (inHouseModelNames.includes(modelName)) {
            return [ 'CLIENT_BYO_LM_CONNECTED_MODEL_NAME_OVERLAPPING_UIPATH_MODEL_ERROR', modelName ];
        }

        if (customerModelNames?.includes(modelName) ?? false) {
            return [ 'CLIENT_BYO_LM_CONNECTED_MODEL_NAME_OVERLAPPING_BYO_MODEL_ERROR', modelName ];
        }
    }

    return null;
};

export const AddEditConfigurationComponentV2: React.FC = () => {
    const organizationId = useSelector(accountGlobalId);
    const {
        tenantId, folderId, id: configurationId,
    } = useParams() as { tenantId: string; folderId: string; id: string };
    const llmConfigurationsReturnUrl = useMemo(() => `${routePaths.adminAiTrustLayerLlmConfigurations}?tenantId=${tenantId}&folderId=${folderId}`, [ folderId, tenantId ]);
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();
    const createNotification = useUiSnackBar();
    const createDialog = useShowDialog();
    const organizationName = useOrganizationName();

    const {
        tenant,
        folder: routeFolder,
        editedProductLlmConfiguration,
        filteredProductConfigurations: productConfigurations,
        llmToConnectorTypeMapping,
        vendorLlmApiFlavors,
        configuredLlms,
        availableLlmsForEditedProductLlmConfiguration,
        integrationServiceConnections,
        isViewLoading: areDependenciesLoading,
    } = useAddEditConfigurationViewModelV2(organizationId, tenantId, folderId, configurationId);

    const breadCrumbLinks = useMemo(() => [
        {
            link: routePaths.admin,
            name: organizationName,
        },
        {
            link: routePaths.adminAiTrustLayer,
            name: translate({ id: 'CLIENT_AI_TRUST_LAYER' }),
        },
        {
            link: llmConfigurationsReturnUrl,
            name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_TAB' }),
        },
        editedProductLlmConfiguration ?
            {
                link: routePaths.adminAiTrustLayerLlmConfigurationsEdit.replace(':id', configurationId),
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_EDIT_CONFIGURATION' }) + `: ${editedProductLlmConfiguration.name}`,
            } :
            {
                link: routePaths.adminAiTrustLayerLlmConfigurationsAdd,
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_ADD_CONFIGURATION' }),
            },
    ], [ configurationId, editedProductLlmConfiguration, llmConfigurationsReturnUrl, organizationName, translate ]);
    const getLocalizedLink = useDocumentationLinks();
    const documentationLink = getLocalizedLink({ articleSlug: '/admin-guide/managing-ai-trust-layer#configuring-llms' });

    const [ isFormDataLoading, setIsFormDataLoading ] = useState<boolean>();
    const [ isConfigurationTestingCommencing, setIsConfigurationTestingCommencing ] = useState<boolean>();
    const [ isConfigurationTested, setIsConfigurationTested ] = useState<boolean>();

    const {
        control,
        getValues,
        handleSubmit,
        resetField,
        setValue,
        setError,
        clearErrors,
        formState: {
            errors, isValid, isSubmitting,
        },
    } = useForm<AddEditConfigurationFormData>({
        mode: 'onTouched',
        defaultValues: {
            folder: null,
            product: '',
            feature: '',
            llmConfigurations: {},
        },
    });

    const {
        folder,
        product,
        feature,
        llmConfigurations,
    } = useWatch({ control });

    const configurationMode = useMemo(() => {
        if (!productConfigurations || !product || !feature || !llmConfigurations || Object.keys(llmConfigurations).length === 0) {
            return undefined;
        }

        if (productConfigurations[product]?.[feature]?.modelsConfigurationOption !== ModelsConfigurationOption.AnyModelWithOwnAdditions) {
            return 'replace';
        }

        const featureLlms = productConfigurations[product][feature].models.map(modelConfig => modelConfig.model);
        const desiredModel = Object.keys(llmConfigurations)[0];

        if (!featureLlms.includes(desiredModel)) {
            return 'add';
        }

        return 'replace';

    }, [ feature, llmConfigurations, product, productConfigurations ]);

    const featureHasAllModelsConfigurationConstraint = useMemo(() => {
        if (!productConfigurations || !product || !feature) {
            return false;
        }
        return productConfigurations[product][feature].modelsConfigurationOption === ModelsConfigurationOption.AllModels;
    }, [ feature, product, productConfigurations ]);

    const isFormComplete = useMemo(() => checkIsFormComplete(llmConfigurations), [ llmConfigurations ]);

    const showFeatureDescription = useMemo(() => {
        if (!product || !feature) {
            return false;
        }
        const featureDescKey = getFeatureDescriptionTranslationKey(product, feature);
        return translationKeyExists(translate, featureDescKey);
    }, [ product, feature, translate ]);

    useEffect(() => {
        if (routeFolder) {
            setValue('folder', routeFolder);
        }
    }, [ configurationId, routeFolder, setValue ]);

    useEffect(() => {
        if (routeFolder && editedProductLlmConfiguration && llmToConnectorTypeMapping && productConfigurations && integrationServiceConnections) {
            setIsFormDataLoading(true);
            setValue('folder', routeFolder);

            if (configurationId) {
                setValue('product', editedProductLlmConfiguration.product);
                setValue('feature', editedProductLlmConfiguration.operationGroupName);

                setValue('llmConfigurations', editedProductLlmConfiguration.llmConfigurations.reduce((acc, llmConfig) => {
                    const mapKey = llmConfig.defaultModel ?? llmConfig.desiredModel;
                    const formConfig = { ...llmConfig };

                    if (formConfig.connectorType === LlmConnectorType.Custom && formConfig.connectionId) {
                        const connection = integrationServiceConnections?.find(c => c.id === formConfig.connectionId);
                        if (connection?.connector?.key) {
                            formConfig.selfServeByoConfigParameters = { connectorTemplate: connection.connector.key };
                            formConfig.connectorTags = connection.connector.tags;
                        }
                    }

                    formConfig.isCustomAlias = !Object.keys(llmToConnectorTypeMapping).includes(llmConfig.desiredModel);

                    acc[mapKey] = formConfig;
                    return acc;
                }, {} as Record<string, Partial<ByoLlmConfiguration>>));
            }

            setIsFormDataLoading(false);
        }
    }, [
        areDependenciesLoading,
        configurationId,
        editedProductLlmConfiguration,
        integrationServiceConnections,
        llmToConnectorTypeMapping,
        productConfigurations,
        routeFolder,
        setValue,
    ]);

    const getCurrentByoProductLlmConfigurationFromValues = useCallback((): Partial<ByoProductLlmConfiguration> => {
        const values = getValues();

        return {
            id: configurationId,
            organizationId,
            tenantId,
            folderId: values.folder?.Key,
            name: 'placeholder-name', // Placeholder name, will be set later
            product: values.product,
            operationGroupName: values.feature,
            llmConfigurations: Object.values(values.llmConfigurations).map(llmConfig => {
                const connectorTemplate = getConnectorTemplateFromTags(llmConfig.connectorTags);

                const config: Partial<ByoLlmConfiguration> = {
                    id: llmConfig.id,
                    organizationId: llmConfig.organizationId ?? organizationId,
                    tenantId: llmConfig.tenantId ?? tenantId,
                    connectionId: llmConfig.connectionId,
                    connectorType: llmConfig.connectorType,
                    apiFlavor: llmConfig.apiFlavor,
                    defaultModel: llmConfig.defaultModel ?? llmConfig.desiredModel,
                    desiredModel: llmConfig.desiredModel,
                    customerModel: llmConfig.customerModel,
                    customFieldMappings: llmConfig.customFieldMappings,
                    configurationType: ByoLlmConfigurationType.SelfServe,
                };

                if (connectorTemplate) {
                    config.selfServeByoConfigParameters = { connectorTemplate };
                }

                return config as ByoLlmConfiguration;
            }),
            enabled: true,
        };
    }, [ configurationId, getValues, organizationId, tenantId ]);

    const validateConfiguration = useCallback(async (request: Partial<ByoProductLlmConfiguration>) => {
        try {
            setIsConfigurationTestingCommencing(true);
            const modelsCompatibilityReports = await validateByoProductLlmConfiguration(
                organizationId,
                tenantId,
                request,
                configurationMode
            );
            const result = processValidationResults(
                modelsCompatibilityReports,
                request.product ?? '',
                request.operationGroupName ?? '',
                llmConfigurations as Record<string, Partial<ByoLlmConfiguration>> | undefined
            );

            if (result.hasIssues) {
                await createDialog({
                    title: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_INVALID_CONFIGURATION_CONNECTED_MODELS_ISSUES_TITLE' }),
                    body: (
                        <ConnectedModelsIssuesDialogBody
                            modelsWithReportedIssues={result.modelsWithReportedIssues} />
                    ),
                    icon: 'error',
                    hideActions: true,
                });
            } else {
                if (llmConfigurations) {
                    setValue('llmConfigurations', llmConfigurations as Record<string, Partial<ByoLlmConfiguration>>); // Update with potential custom field mappings
                }
                setIsConfigurationTested(true);

                await createDialog({
                    title: '',
                    customDialogContent: ConnectedModelsSuccessDialogBody,
                    hideActions: true,
                    unclosable: true,
                });
            }
        } catch (error: any) {
            createNotification(translate(
                { id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_VALIDATE' },
                { error: error?.response?.data?.message ?? error?.message }
            ), 'error');
        } finally {
            setIsConfigurationTestingCommencing(false);
        }
    }, [ createDialog, createNotification, configurationMode, llmConfigurations, organizationId, setValue, tenantId, translate ]);

    const modifyConfiguration = useCallback(async (request: Partial<ByoProductLlmConfiguration>) => {
        try {
            if (configurationId) {
                await updateByoProductLlmConfiguration(organizationId, tenantId, configurationId, request);
            } else {
                await addByoProductLlmConfiguration(organizationId, tenantId, request);
            }
            navigate(llmConfigurationsReturnUrl);
        } catch (error: any) {
            const stringIdentifier = configurationId
                ? 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_EDIT'
                : 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_ADD';
            createNotification(translate({ id: stringIdentifier }, { error: error?.response?.data?.message ?? error?.message }), 'error');
        }
    }, [ configurationId, createNotification, llmConfigurationsReturnUrl, navigate, organizationId, tenantId, translate ]);

    const onClose = useCallback(async () => {
        navigate(llmConfigurationsReturnUrl);
    }, [ llmConfigurationsReturnUrl, navigate ]);

    const onTest = useCallback(async () => {
        const request = getCurrentByoProductLlmConfigurationFromValues();

        if (!isValid) {
            return;
        }

        // Placeholder name
        request.name = `validation-${request.operationGroupName}-${Date.now()}`;
        await validateConfiguration(request);
    }, [ getCurrentByoProductLlmConfigurationFromValues, isValid, validateConfiguration ]);

    const onSubmit = useCallback(async () => {
        const request = getCurrentByoProductLlmConfigurationFromValues();

        if (!isValid || !isConfigurationTested) {
            return;
        }

        request.name = `${request.operationGroupName}-${Date.now()}`;
        await modifyConfiguration(request);
    }, [ getCurrentByoProductLlmConfigurationFromValues, isConfigurationTested, isValid, modifyConfiguration ]);

    const validateLlmConfigurationsRequiredFields = useCallback(async (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => {
        const errorId = detectIncompleteLlmConfigurationErrors(llmConfigs, false);

        if (errorId) {
            return translate({ id: errorId });
        }

        // Manually clear errors generated outside controlled validation
        clearErrors('llmConfigurations');
        return true;
    }, [ clearErrors, translate ]);

    const validateLlmConfigurationsModelNameOverlap = useCallback(async (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => {
        const featureConfig = productConfigurations?.[product ?? '']?.[feature ?? ''];

        if (!featureConfig || !llmConfigs || Object.values(llmConfigs).length === 0) {
            return true;
        }

        const originalLlmConfigs = editedProductLlmConfiguration?.llmConfigurations ?? [];
        const inHouseModelNames = Object.keys(llmToConnectorTypeMapping);
        const customerModelNames = configuredLlms?.[product ?? '']?.[feature ?? ''] ?? [];

        const result = detectModelOverlapErrors(featureConfig, Object.values(llmConfigs), originalLlmConfigs, inHouseModelNames, customerModelNames);

        if (result) {
            const [ errorId, modelName ] = result;
            return translate({ id: errorId }) + `,${modelName}`;
        }

        // Manually clear errors generated outside controlled validation
        clearErrors('llmConfigurations');
        return true;
    }, [ clearErrors, configuredLlms, editedProductLlmConfiguration?.llmConfigurations, feature, llmToConnectorTypeMapping, product, productConfigurations, translate ]);

    // V2 UI - Modify the JSX below to implement your new design
    return (
        <UiPageContainer
            loading={areDependenciesLoading || isFormDataLoading}
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadCrumbLinks} />}
            position='left'>
            <UiForm
                onSubmit={handleSubmit(onSubmit)}
                addScrollPadding
                actions={
                    <UiStack
                        direction="row"
                        justify="end"
                        gap={SpacingToken.S}>
                        <ApButton
                            label={translate({ id: 'CLIENT_BYO_LLM_CONFIGURE_TEST' })}
                            variant='primary'
                            disabled={!isFormComplete || !isValid || Object.keys(errors).length > 0 || isConfigurationTested}
                            loading={isConfigurationTestingCommencing}
                            type="button"
                            onClick={onTest}
                            data-cy="add-edit-llm-configuration-test-button" />
                        <ApButton
                            label={translate({ id: 'CLIENT_CANCEL' })}
                            variant='secondary'
                            onClick={() => onClose()}
                            data-cy="add-edit-llm-configuration-cancel-button" />
                        <ApButton
                            label={translate({ id: 'CLIENT_SAVE' })}
                            variant='primary'
                            disabled={!isConfigurationTested}
                            loading={isSubmitting}
                            type="submit"
                            data-cy="add-edit-llm-configuration-save-button" />
                    </UiStack>
                }>
                <UiStack
                    h='100%'
                    direction='column'
                    mt={SpacingToken.Base}
                    gap={SpacingToken.L}>
                    <UiStack
                        direction='column'
                        gap={SpacingToken.L}>
                        <AddEditConfigurationHeaderSection
                            control={control}
                            resetField={resetField}
                            setValue={setValue}
                            setError={setError}
                            tenantName={tenant?.name}
                            product={product}
                            notifyFieldChanges={() => setIsConfigurationTested(false)}
                            productConfigurations={productConfigurations ?? {}}
                            replaceableLlms={availableLlmsForEditedProductLlmConfiguration ?? {}}
                            isEditMode={!!configurationId}
                        />
                        {product && feature && (
                            <>
                                <ApAlertBar
                                    status="info"
                                    cancelable
                                    style={{ maxWidth: '1000px' }}
                                >
                                    <FormattedMessage
                                        id='CLIENT_BYO_LLM_CONFIGURE_FEATURE_DESCRIPTION'
                                        values={{
                                            documentationLink: (msg: React.ReactNode[]) =>
                                                (
                                                    <ApLink
                                                        href={documentationLink}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                    >
                                                        {msg}
                                                    </ApLink>
                                                ),
                                        }}
                                    />
                                </ApAlertBar>
                                <Controller
                                    name="folder"
                                    control={control}
                                    rules={{ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }}
                                    render={({ field }) => (
                                        <FolderDropdown
                                            tenant={tenant}
                                            value={field.value ?? undefined}
                                            required
                                            disableAutoSelectFirstOption
                                            onChange={(newValue) => {
                                                field.onChange(newValue);

                                                if (featureHasAllModelsConfigurationConstraint) {
                                                    setValue('llmConfigurations', availableLlmsForEditedProductLlmConfiguration?.[product][feature]
                                                        .reduce((acc, llm) => {
                                                            acc[llm] = {
                                                                desiredModel: llm,
                                                                defaultModel: llm,
                                                            };
                                                            return acc;
                                                        }, {} as Record<string, Partial<ByoLlmConfiguration>>) ?? {});
                                                } else {
                                                    resetField('llmConfigurations');
                                                }

                                                setIsConfigurationTested(false);
                                            }}
                                            labelTooltip={{
                                                translationKey: 'CLIENT_BYO_LLM_FOLDER_TOOLTIP',
                                                placement: 'right',
                                            }} />
                                    )}
                                />
                                <UiStack
                                    direction='column'
                                    gap={SpacingToken.XS}
                                    mt={SpacingToken.M}
                                    mb={SpacingToken.S}>
                                    <UiText variant={FontVariantToken.fontSizeLBold}>
                                        {translate({ id: 'CLIENT_BYO_LLM_MODEL_CONFIGURATION' })}
                                    </UiText>
                                    {showFeatureDescription && (
                                        <UiText>
                                            {translate({ id: getFeatureDescriptionTranslationKey(product, feature) })}
                                        </UiText>
                                    )}
                                </UiStack>
                                <Controller
                                    name="llmConfigurations"
                                    control={control}
                                    rules={{
                                        required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }),
                                        validate: {
                                            [ByoFormErrorTypes.ModelNameOverlap]: validateLlmConfigurationsModelNameOverlap,
                                            [ByoFormErrorTypes.RequiredField]: validateLlmConfigurationsRequiredFields,
                                        },
                                    }}
                                    render={({
                                        field, fieldState,
                                    }) =>
                                        featureHasAllModelsConfigurationConstraint
                                            ? <AllModelsConfigurationSection
                                                organizationId={organizationId}
                                                tenantId={tenantId}
                                                folderKey={folder?.Key}
                                                llmConfigurationsField={field}
                                                notifyFieldChanges={() => setIsConfigurationTested(false)}
                                                llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                                                vendorLlmApiFlavors={vendorLlmApiFlavors}
                                                product={product}
                                                feature={feature}
                                                productConfigurations={productConfigurations}
                                                error={fieldState.error}
                                            />
                                            : <AnyModelConfigurationSection
                                                organizationId={organizationId}
                                                tenantId={tenantId}
                                                folderKey={folder?.Key}
                                                modelsConfigurationOption={productConfigurations?.[product]?.[feature]?.modelsConfigurationOption}
                                                llmConfigurationsField={field}
                                                notifyFieldChanges={() => setIsConfigurationTested(false)}
                                                llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                                                vendorLlmApiFlavors={vendorLlmApiFlavors}
                                                replaceableLlms={availableLlmsForEditedProductLlmConfiguration?.[product][feature] ?? []}
                                                product={product}
                                                feature={feature}
                                                productConfigurations={productConfigurations}
                                                error={fieldState.error}
                                            />}
                                />
                            </>
                        )}
                    </UiStack>
                </UiStack>
            </UiForm>
        </UiPageContainer>
    );
};
